import React, { useEffect, useRef } from 'react';
import { useFarcasterMiniApp } from '../../hooks/useFarcasterMiniApp';
import LoadingSpinner from './LoadingSpinner';

/**
 * FarcasterSplashScreen Component
 * 
 * Manages the Farcaster Mini App splash screen according to best practices:
 * - Shows branded loading screen while the app initializes
 * - Calls sdk.actions.ready() only when the interface is fully loaded
 * - Avoids jitter and content reflows
 * - Provides fallback for regular web usage
 * 
 * This component should wrap your main app content and will automatically
 * handle the transition from splash screen to your app interface.
 */
const FarcasterSplashScreen = ({ 
  children, 
  onReady, 
  disableNativeGestures = false,
  minLoadingTime = 1000 // Minimum time to show splash screen to avoid flash
}) => {
  const { isReady, isMiniApp, isLoading, error, callReady } = useFarcasterMiniApp();
  const startTimeRef = useRef(Date.now());
  const [showContent, setShowContent] = React.useState(false);
  const [logoLoaded, setLogoLoaded] = React.useState(false);
  const [logoError, setLogoError] = React.useState(false);
  
  useEffect(() => {
    if (isReady && !isLoading) {
      const elapsed = Date.now() - startTimeRef.current;
      const remainingTime = Math.max(0, minLoadingTime - elapsed);
      
      const timer = setTimeout(() => {
        // Call the Farcaster ready function if we're in a mini app
        if (isMiniApp) {
          callReady(disableNativeGestures);
        }
        
        // Show the main content
        setShowContent(true);
        
        // Call optional onReady callback
        if (onReady) {
          onReady();
        }
      }, remainingTime);
      
      return () => clearTimeout(timer);
    }
  }, [isReady, isLoading, isMiniApp, callReady, disableNativeGestures, onReady, minLoadingTime]);

  // Show splash screen while loading or before content is ready
  if (!showContent) {
    return (
      <div className="min-h-screen bg-castoria-gradient flex items-center justify-center relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-white rounded-full blur-2xl" />
        </div>
        
        <div className="text-center space-y-6 px-4 max-w-sm mx-auto relative z-10">
          {/* Castoria Logo */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              {logoError ? (
                // Fallback to emoji if logo fails to load
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                  <div className="text-4xl">🏰</div>
                </div>
              ) : (
                <img 
                  src="/castoria-logo.png" 
                  alt="Castoria Logo" 
                  className={`w-24 h-24 object-contain transition-opacity duration-300 ${
                    logoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))' }}
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => setLogoError(true)}
                />
              )}
              
              {/* Loading placeholder for image */}
              {!logoLoaded && !logoError && (
                <div className="absolute inset-0 w-24 h-24 bg-white/10 rounded-full animate-pulse" />
              )}
              
              {/* Sparkle effects */}
              <div className="absolute -top-2 -right-2 w-3 h-3 bg-white rounded-full opacity-80 animate-pulse" />
              <div className="absolute -top-1 right-6 w-2 h-2 bg-white rounded-full opacity-60 animate-pulse delay-300" />
            </div>
            
            {/* App Name & Tagline */}
            <div className="space-y-1">
              <h1 className="text-white font-bold text-3xl tracking-tight">
                Castoria
              </h1>
              <p className="text-white/90 text-sm font-medium">
                The Social Fortress for NFTs
              </p>
            </div>
          </div>
          
          {/* Loading State */}
          <div className="space-y-4">
            {error ? (
              <div className="text-red-300 text-sm font-medium">
                Failed to initialize app
              </div>
            ) : isLoading ? (
              <div className="text-white/80 text-sm font-medium">
                Initializing Fortress...
              </div>
            ) : (
              <div className="text-white/80 text-sm font-medium">
                {isMiniApp ? 'Loading Mini App...' : 'Entering Fortress...'}
              </div>
            )}
            
            <div className="flex justify-center">
              <LoadingSpinner size="md" />
            </div>
          </div>
          
          {/* Mini App indicator */}
          {isMiniApp && (
            <div className="flex items-center justify-center space-x-2 text-white/60 text-xs">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Connected to Farcaster</span>
            </div>
          )}
          
          {/* Version/Brand footer */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="text-white/40 text-xs font-medium">
              Powered by Farcaster • v1.0
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show main app content
  return children;
};

export default FarcasterSplashScreen; 